import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { API_BASE } from "@/lib/api";

/**
 * Live status pill - polls /api/health every 30s. Falls open (shows "checking...")
 * on network error rather than blocking render. Public component: no auth.
 * Clicking navigates to /trust for the full status detail.
 */
export default function StatusPill({ className = "", size = "md" }) {
  const [state, setState] = useState({ status: "checking", label: "Checking status..." });

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API_BASE}/health`, { credentials: "omit" });
        if (!r.ok) throw new Error(String(r.status));
        const j = await r.json();
        if (cancelled) return;
        if (j.status === "operational") setState({ status: "operational", label: "All systems operational" });
        else setState({ status: "degraded", label: "Degraded performance" });
      } catch {
        if (!cancelled) setState({ status: "unknown", label: "Status unavailable" });
      }
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const palette = {
    operational: { dot: "bg-emerald-500", ring: "ring-emerald-500/30", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", hover: "hover:bg-emerald-100", glow: "shadow-[0_0_10px_rgba(16,185,129,0.65)]" },
    degraded:    { dot: "bg-amber-500",   ring: "ring-amber-500/30",   text: "text-amber-800",   bg: "bg-amber-50",   border: "border-amber-200",   hover: "hover:bg-amber-100",   glow: "shadow-[0_0_10px_rgba(245,158,11,0.65)]" },
    checking:    { dot: "bg-slate-400",   ring: "ring-slate-400/30",   text: "text-slate-600",   bg: "bg-slate-50",   border: "border-slate-200",   hover: "hover:bg-slate-100",   glow: "" },
    unknown:     { dot: "bg-slate-400",   ring: "ring-slate-400/30",   text: "text-slate-500",   bg: "bg-slate-50",   border: "border-slate-200",   hover: "hover:bg-slate-100",   glow: "" },
  }[state.status];

  const pad = size === "sm" ? "px-2.5 py-1" : "px-3 py-1.5";
  const dotSize = size === "sm" ? "h-1.5 w-1.5" : "h-2 w-2";
  const textSize = size === "sm" ? "text-[11px]" : "text-[12.5px]";

  return (
    <Link
      to="/trust"
      className={`inline-flex items-center gap-2 rounded-full border ${palette.bg} ${palette.border} ${palette.hover} ${pad} transition-colors ${className}`}
      data-testid="status-pill"
      data-status={state.status}
      title={`${state.label} - open Trust page`}
    >
      <span className={`relative flex ${dotSize}`}>
        <span className={`absolute inline-flex h-full w-full rounded-full ${palette.dot} opacity-60 ${state.status === "operational" ? "animate-ping" : ""}`} />
        <span className={`relative inline-flex ${dotSize} rounded-full ${palette.dot} ${palette.glow}`} />
      </span>
      <span className={`${textSize} font-medium ${palette.text}`}>{state.label}</span>
    </Link>
  );
}
